import { useApi } from "@/hooks/useApi";
import { useGodMode } from "@/hooks/useGodMode";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  DeepSearchQuotaDTO,
  ForcedSearchTool,
  SearchMode,
} from "@shaarp/common";
import { useCallback, useState } from "react";
import {
  Building2Icon,
  BriefcaseIcon,
  CheckIcon,
  FileTextIcon,
  GlobeIcon,
  HelpCircleIcon,
  LandmarkIcon,
  MapPinIcon,
  PlusIcon,
  RadarIcon,
  TelescopeIcon,
  UsersIcon,
} from "lucide-react";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

type ToolOption = {
  id: ForcedSearchTool;
  labelKey: string;
  descriptionKey?: string;
  icon: React.ReactNode;
};

/** The deep-search tool, the only one subject to a per-user weekly quota. */
const DEEP_SEARCH_TOOL: ForcedSearchTool = "search_companies_web_deep";

const TOOL_OPTIONS: ToolOption[] = [
  {
    id: "search_companies_web",
    labelKey: "prospecting.toolSelector.searchWeb",
    descriptionKey: "prospecting.toolSelector.searchWebDescription",
    icon: <GlobeIcon className="size-4" />,
  },
  {
    id: "search_companies_web_deep",
    labelKey: "prospecting.toolSelector.searchWebDeep",
    descriptionKey: "prospecting.toolSelector.searchWebDeepDescription",
    icon: <TelescopeIcon className="size-4" />,
  },
];

// Specialized provider tools — only shown to (and forceable by) god users, for
// testing/validation. The server independently rejects these from non-gods.
const GOD_TOOL_OPTIONS: ToolOption[] = [
  {
    id: "search_companies_coresignal",
    labelKey: "prospecting.toolSelector.coresignal",
    icon: <Building2Icon className="size-4" />,
  },
  {
    id: "search_companies_coresignal_jobs",
    labelKey: "prospecting.toolSelector.coresignalJobs",
    icon: <BriefcaseIcon className="size-4" />,
  },
  {
    id: "search_employees_coresignal",
    labelKey: "prospecting.toolSelector.employees",
    icon: <UsersIcon className="size-4" />,
  },
  {
    id: "search_companies_pappers",
    labelKey: "prospecting.toolSelector.pappers",
    icon: <FileTextIcon className="size-4" />,
  },
  {
    id: "search_companies_scrapio",
    labelKey: "prospecting.toolSelector.scrapio",
    icon: <MapPinIcon className="size-4" />,
  },
  {
    id: "search_companies_gouv",
    labelKey: "prospecting.toolSelector.gouv",
    icon: <LandmarkIcon className="size-4" />,
  },
];

export const SearchToolSelector = ({
  value,
  onChange,
  searchMode,
  onSearchModeChange,
  disabled = false,
}: {
  value: ForcedSearchTool | null;
  onChange: (tool: ForcedSearchTool | null) => void;
  searchMode: SearchMode;
  onSearchModeChange: (mode: SearchMode) => void;
  disabled?: boolean;
}) => {
  const { t, currentLanguage } = useTranslation();
  const api = useApi();
  const isGodMode = useGodMode();

  const [deepSearchQuota, setDeepSearchQuota] =
    useState<DeepSearchQuotaDTO | null>(null);

  // Refreshed each time the menu opens so the counter reflects searches the
  // user just ran. Non-critical: on failure we simply hide the counter.
  const fetchDeepSearchQuota = useCallback(async () => {
    try {
      const { data } = await api.get<DeepSearchQuotaDTO>(
        "/search-engine/deep-search/quota",
      );
      setDeepSearchQuota(data);
    } catch {
      setDeepSearchQuota(null);
    }
  }, [api]);

  const allOptions = [...TOOL_OPTIONS, ...GOD_TOOL_OPTIONS];
  const selectedOption = value
    ? allOptions.find((o) => o.id === value)
    : null;

  const renderOption = (option: ToolOption) => {
    const isSelected = value === option.id;
    const showQuota = option.id === DEEP_SEARCH_TOOL && deepSearchQuota !== null;
    const isQuotaReached =
      deepSearchQuota !== null && deepSearchQuota.used >= deepSearchQuota.limit;
    return (
      <DropdownMenuItem
        key={option.id}
        onClick={() => onChange(isSelected ? null : option.id)}
        className={cn(
          "gap-2.5 py-2.5 cursor-pointer",
          isSelected && "bg-majorelle/10 text-majorelle",
        )}
      >
        <span className="flex justify-center items-center size-5">
          {option.icon}
        </span>
        <span>{t(option.labelKey)}</span>
        {option.descriptionKey && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <HelpCircleIcon className="size-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[240px]">
                {t(option.descriptionKey)}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <span className="flex-1" />
        {showQuota && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant={isQuotaReached ? "destructive" : "secondary"}
                  className="tabular-nums"
                >
                  {deepSearchQuota.used}/{deepSearchQuota.limit}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {isQuotaReached
                  ? t("prospecting.toolSelector.deepSearchQuotaTooltipReached", {
                      limit: deepSearchQuota.limit,
                      resetDate: new Date(
                        deepSearchQuota.resetsAt,
                      ).toLocaleDateString(currentLanguage),
                    })
                  : t("prospecting.toolSelector.deepSearchQuotaTooltip", {
                      remaining: deepSearchQuota.limit - deepSearchQuota.used,
                      limit: deepSearchQuota.limit,
                    })}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {isSelected && <CheckIcon className="size-4 text-majorelle" />}
      </DropdownMenuItem>
    );
  };

  const isBroadMode = searchMode === "broad";

  const renderBroadenOption = () => (
    <DropdownMenuItem
      onClick={() => onSearchModeChange(isBroadMode ? "strict" : "broad")}
      className={cn(
        "gap-2.5 py-2.5 cursor-pointer",
        isBroadMode && "bg-majorelle/10 text-majorelle",
      )}
    >
      <span className="flex justify-center items-center size-5">
        <RadarIcon className="size-4" />
      </span>
      <span>{t("prospecting.toolSelector.broadenSearch")}</span>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              onClick={(e) => e.stopPropagation()}
              className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <HelpCircleIcon className="size-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[240px]">
            {t("prospecting.toolSelector.broadenSearchDescription")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <span className="flex-1" />
      {isBroadMode && <CheckIcon className="size-4 text-majorelle" />}
    </DropdownMenuItem>
  );

  return (
    <div className="flex gap-1 items-center shrink-0">
      {isBroadMode && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onSearchModeChange("strict")}
                className={cn(
                  "flex justify-center items-center rounded-full transition-all shrink-0 size-9",
                  "text-white bg-majorelle/50 hover:bg-majorelle/65",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                <RadarIcon className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {t("prospecting.toolSelector.broadenSearch")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {selectedOption && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(null)}
                className={cn(
                  "flex justify-center items-center rounded-full transition-all shrink-0 size-9",
                  "text-white bg-majorelle/50 hover:bg-majorelle/65",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                {selectedOption.icon}
              </button>
            </TooltipTrigger>
            <TooltipContent>{t(selectedOption.labelKey)}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <DropdownMenu
        onOpenChange={(open) => {
          if (open) void fetchDeepSearchQuota();
        }}
      >
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={disabled}
                  className={cn(
                    "flex justify-center items-center rounded-full transition-colors shrink-0 size-9",
                    "text-muted-foreground hover:text-foreground hover:bg-muted",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                >
                  <PlusIcon className="size-5" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              {t("prospecting.toolSelector.forceTools")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DropdownMenuContent
          align="end"
          sideOffset={12}
          className="min-w-[220px]"
        >
          {renderBroadenOption()}
          <DropdownMenuSeparator />
          {TOOL_OPTIONS.map(renderOption)}
          {isGodMode && (
            <>
              <DropdownMenuSeparator />
              <div className="god-mode-section">
                <DropdownMenuLabel className="god-mode-label text-xs">
                  {t("prospecting.toolSelector.godMode")}
                </DropdownMenuLabel>
                {GOD_TOOL_OPTIONS.map(renderOption)}
              </div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
